import { useIsFeatureEnabled } from '@innovyze/stylovyze';
import { useFilters } from '@Map/esri/features/useFilters';
import {
	selectSelectedAssetType,
	selectSelectedSystemType,
} from '@Map/esri/features/useFilters/filterSlice';
import { useSelector } from 'react-redux';
import { useFilter as useMapComponentsFilter } from '@innovyze/map-components';

/**
 * Unified filter hook that works in both Esri and Mapbox modes.
 *
 * In **Esri mode** the returned object is sourced from the Redux filter slice
 * and is compatible with the `FilterContextProps` shape from
 * `@innovyze/map-components` so consuming apps need no code changes beyond
 * updating the import path.
 *
 * In **Mapbox mode** the hook delegates directly to
 * `@innovyze/map-components`'s `useFilter` and returns its result unchanged.
 *
 * Note on Rules of Hooks: `isEsri` is derived from a feature flag that is
 * stable for the lifetime of the app (it never changes between renders).
 * Calling a different hook based on this stable value is therefore safe —
 * hook call order is consistent across all renders of any given component
 * instance.
 */
export const useFilter = () => {
	const isEsri = Boolean(useIsFeatureEnabled('info360-platform-esri-map'));

	if (isEsri) {
		const filters = useFilters();
		const selectedAssetType = useSelector(selectSelectedAssetType);
		const selectedSystemType = useSelector(selectSelectedSystemType);

		return {
			rawFilter: filters.rawFilter,
			polygonFilter: filters.polygonFilter,
			excludedFilter: filters.excludedFilter,
			filterCount: filters.filterCount,
			clearFilters: filters.clearFilters,
			setPolygonFilter: filters.setPolygonFilter,
			setFilterModel: filters.setFilterModel,
			additionalFilterProps: {
				assetType: selectedAssetType,
				systemType: selectedSystemType,
			},
		};
	}

	// Mapbox mode: delegate to @innovyze/map-components
	return useMapComponentsFilter();
};
